import { useAuthorStore } from '@/stores';

/**
 * string|array|object|async method转数组
 * @param input
 * @param methodParams
 * @returns {Promise<Awaited<[]>|Awaited<Boolean>>}
 */
const any2Array = async (input, methodParams) => {
    let result = [];
    if (typeof input === 'string') {
        result = (input || '').split(',');
    } else if (Array.isArray(input)) {
        result = input;
    } else if (typeof input === 'object') {
        result = Object.keys((input || {})).reduce((prev, k) => {
            const v = input[k];
            if (typeof v !== 'undefined' && v) {
                prev.push(k);
            }
            return prev;
        }, []);
    } else if (typeof input === 'function') {
        result = await input(methodParams);
    } else {
        return Promise.resolve(false);
    }
    return Promise.resolve(result.filter(s => !!s));
};

/**
 * arrayIsMatch 检查是否匹配数组项
 * @param expectArr 期望的数组项
 * @param testArr 测试的数组项
 * @param logic 匹配逻辑,and|or其他都当and处理
 *  logic：and时testArr中包含所有expectArr中的项返回true,否则返回false
 *         or时testArr中只要包含expectArr中的任一项返回true,否则返回false
 * @returns {Boolean}
 */
const arrayIsMatch = (expectArr = [], testArr = [], logic = 'and') => {
    let result = true;
    for (const item of expectArr) {
        const b = testArr.includes(item);
        result = result && b;
        if (logic === 'or' && b) {
            return true;
        }
    }
    return result; // and时都含为true,or时都不匹配为false
};
/**
 * useRouterPermission 路由权限守卫
 * @param router
 * @param options
 * @returns {void}
 */
export const useRouterPermission = (router, options = {
    loginPage: '/login',
    rolePermError: '/rolePermError', // 角色无权操作
    permError: '/permError', // 无权操作
}) => {
    /**
     * 是否匹配角色 expectRoles为或关系,testRoles中包含expectRoles中的任何一项则返回true
     * @param token
     * @param expectRoles String|Array|Object|Awaited<Function>
     * @param testRoles String|Array|Object|Awaited<Function>
     * @returns {Promise<Awaited<boolean>>}
     */
    const isMatchRoles = async (token, expectRoles = [], testRoles = []) => {
        const expect = await any2Array(expectRoles, { token });
        const test = await any2Array(testRoles, { token });
        if (!expect || (expect || []).length === 0) return Promise.resolve(true); // 不要求检查角色
        if (!test || (test || []).length === 0) return Promise.resolve(false); // 要求角色，未提供角色
        const result = arrayIsMatch(expect || [], test || [], 'or');
        return Promise.resolve(result);
    };

    /**
     * 检查是否有权限项，expectPerms列表为或关系，testPerms中包含expectPerms中的任何一项则返回true
     * @param token
     * @param expectPerms String|Array|Object|Awaited<Function>
     * @param testPerms String|Array|Object|Awaited<Function>
     * @returns {Promise<Awaited<Boolean>>}
     */
    const hasPermission = async (token, expectPerms, testPerms) => {
        const expect = await any2Array(expectPerms, { token });
        const test = await any2Array(testPerms, { token });
        if (!expect) return Promise.resolve(true);
        if (!test) return Promise.resolve(false);
        const result = arrayIsMatch(expect || [], test || [], 'or');
        return Promise.resolve(result);
    };

    router.beforeEach(async (to, from, next) => {
        const {
            loginPage = '/login',
            rolePermError = '/rolePermError', // 角色不匹配提示页
            permError = '/permError', // 权限不足提示页
        } = options || {};
        const authorStore = useAuthorStore();
        const {
            path,
            meta: {
                requireAuth = false, // 要求授权
                roles: expectRoles,
                perms: expectPerms, // 要求的权限项
            } = {},
        } = to;
        const { token, roles, perms } = authorStore;
        if (requireAuth && !token) {
            // 未登录，跳转到登录页
            if (to.path !== `${loginPage}`) {
                return next(`${loginPage}?redirect=${path}`);
            }
            return next();
        } else if (requireAuth && token) {
            // 已登录，检查角色和权限
            const hasRole = await isMatchRoles(token, expectRoles, roles);
            if (!hasRole && to.path !== `${rolePermError}`) {
                return next(`${rolePermError}?from=${from.path}`);
            }
            const hasPerm = await hasPermission(token, expectPerms, perms);
            if (!hasPerm && to.path !== `${permError}`) {
                return next(`${permError}?from=${from.path}`);
            }
            // 检查通过
            if (path === `${loginPage}`) {
                return next('/'); // 已经登录时，输入登录页面时跳转到首页
            }
        }
        // 不需要权限或权限正确，正常跳转
        return next(); // 正常跳转
    });
};
